import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { Button } from './common/Catalyst/button';
import { NotificationResponse } from './clients/rust-api-client';
import { notificationService } from './clients/ApiClients';
import LoadingSpinner from './LoadingSpinner';
import ShowAlert from './ShowAlert';
import {
  Dialog,
  DialogBody,
  DialogTitle,
  DialogActions,
} from './common/Catalyst/dialog';
import { Fact, Section } from './violation/linear/panelParts';
import {
  entityPath,
  isStaffNotification,
  notificationTypeLabel,
} from './notification/notificationMeta';
import { getUserIdFromToken } from './utils/TokenUtils';
import { formatTimestamp } from './utils/dateUtils';
import { apiErrorMessage } from './utils/apiErrors';
import { useSyncedNotification } from './sync/hooks';
import { setNotificationsRead } from './sync/writes';
import { ErrorPage } from './common/Custom/ErrorPage';

interface AlertState {
  type: 'success' | 'error';
  title: string;
  message: string;
  show: boolean;
}

const linkClass = 'text-accent-fg hover:underline';
const idClass = 'font-mono text-[12px]';

/**
 * One staff notification (#624), with every column of its row. A row sent
 * to a homeowner is a communication: its read state is the homeowner's,
 * so it gets no actions here (#626) and points to Communications (#623).
 */
const NotificationDetailPage: React.FC = () => {
  const { notificationId } = useParams<{ notificationId: string }>();
  const navigate = useNavigate();
  const [ownNotification, setNotification] = useState<NotificationResponse | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Why the notification could not be loaded, and a counter "Try again"
  // bumps to run the load again.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [alert, setAlert] = useState<AlertState>({
    type: 'success',
    title: '',
    message: '',
    show: false,
  });

  const showAlert = (
    type: 'success' | 'error',
    title: string,
    message: string
  ): void => {
    setAlert({
      type,
      title,
      message,
      show: true,
    });
  };

  // A staff notification is in the sync engine's copy (#561), current as
  // it changes; one sent to a homeowner is fetched.
  const synced = useSyncedNotification(notificationId);
  const haveSynced = !!synced;
  const notification = synced ?? ownNotification;

  useEffect(() => {
    const fetchNotification = async (): Promise<void> => {
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setLoadError(null);
        const response = await notificationService.getNotificationById({
          notificationId: notificationId!,
        });
        setNotification(response.data);
      } catch (err) {
        console.error('Error fetching notification:', err);
        setLoadError(apiErrorMessage(err, "Couldn't load the notification."));
      } finally {
        setLoading(false);
      }
    };

    if (notificationId) {
      fetchNotification();
    }
  }, [notificationId, reloadKey, haveSynced]);

  const handleToggleReadStatus = async (): Promise<void> => {
    if (!notification) return;

    try {
      setIsUpdating(true);
      // Through the write queue (#561): a staff notification in the
      // synced copy changes everywhere at once; anything else is a plain
      // request.
      const isRead = !notification.is_read;
      await setNotificationsRead([notification.id], isRead);
      setNotification({ ...notification, is_read: isRead });
    } catch (err) {
      console.error('Error updating notification:', err);
      const errorMessage = apiErrorMessage(
        err,
        "Couldn't update the notification."
      );
      showAlert('error', "Couldn't update", errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!notification) return;

    try {
      setIsDeleting(true);
      await notificationService.deleteNotification({
        notificationId: notification.id,
      });
      setShowDeleteDialog(false);
      navigate('/notifications');
    } catch (err) {
      console.error('Error deleting notification:', err);
      const errorMessage = apiErrorMessage(
        err,
        "Couldn't delete the notification."
      );
      showAlert('error', "Couldn't delete", errorMessage);
      setIsDeleting(false);
    }
  };

  if (loading && !haveSynced) {
    return <LoadingSpinner />;
  }

  // A failed load leaves nothing to show; say so rather than render an
  // empty page (#652).
  if (!notification) {
    return (
      <SidebarLayout
        sidebar={<AppSidebar currentPage="notifications" />}
        navbar={<Navbar />}
      >
        <ErrorPage
          title="Couldn't load this notification"
          message={
            loadError ??
            'It may have been deleted, or you may not have access to it.'
          }
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </SidebarLayout>
    );
  }

  const isStaff = notification ? isStaffNotification(notification) : false;
  const about = notification ? entityPath(notification) : null;
  const isMine =
    !!notification?.user_id && notification.user_id === getUserIdFromToken();

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="notifications" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link to="/notifications" className="text-fg-muted hover:text-fg">
          Notifications
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        <span className="min-w-0 truncate font-medium text-fg">
          {notification?.title ?? 'Notification not found'}
        </span>
        {notification && isStaff && (
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <Button
              outline
              onClick={handleToggleReadStatus}
              disabled={isUpdating}
            >
              {isUpdating
                ? 'Updating...'
                : notification.is_read
                  ? 'Mark as unread'
                  : 'Mark as read'}
            </Button>
            <Button
              color="red"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </div>
        )}
      </div>

      <ShowAlert
        alertType={alert.type}
        alertTitle={alert.title}
        alertMessage={alert.message}
        isVisible={alert.show}
        onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
        timeout={5000}
        isInline={true}
      />

      {notification && notification.is_for_member && (
        <p className="mb-6 text-[13px] text-fg-secondary">
          This was sent to a homeowner, so it lives in{' '}
          <Link to={`/communications/${notification.id}`} className={linkClass}>
            Communications
          </Link>
          . Its read state is the homeowner&apos;s.
        </p>
      )}

      {notification && (
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="min-w-0">
            <h1 className="text-2xl/8 font-semibold tracking-tight text-fg">
              {notification.title}
            </h1>
            <p className="mt-1 text-[13px] text-fg-muted">
              For {isMine ? 'you' : notification.recipient_name} ·{' '}
              {formatTimestamp(notification.created_at)}
            </p>
            <p className="mt-4 whitespace-pre-line text-[15px]/6 text-fg-secondary">
              {notification.message}
            </p>
            {about && (
              <p className="mt-4 text-[13px]">
                <Link to={about} className={linkClass}>
                  Open the {notification.entity_type} →
                </Link>
              </p>
            )}
          </div>

          <aside
            aria-label="Notification properties"
            className="lg:sticky lg:top-6 lg:self-start"
          >
            <Section title="Recipient">
              <Fact label="Name">{notification.recipient_name}</Fact>
              <Fact label="Email">{notification.recipient_email || '—'}</Fact>
              <Fact label="HOA">
                <Link to={`/hoa/${notification.hoa_id}`} className={linkClass}>
                  {notification.hoa_name}
                </Link>
              </Fact>
            </Section>
            <Section title="Status">
              <Fact label="Read">{notification.is_read ? 'Yes' : 'No'}</Fact>
              <Fact label="Email">
                {notification.email_sent ? 'Sent' : 'Not sent'}
              </Fact>
            </Section>
            <Section title="About">
              <Fact label="Type">
                {notificationTypeLabel(notification.notification_type)}
              </Fact>
              <Fact label="Entity">
                {notification.entity_id ? (
                  <>
                    {notification.entity_type}{' '}
                    {about ? (
                      <Link to={about} className={`${linkClass} ${idClass}`}>
                        {notification.entity_id}
                      </Link>
                    ) : (
                      <span className={idClass}>{notification.entity_id}</span>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </Fact>
            </Section>
            <Section title="Record">
              <Fact label="ID">
                <span className={idClass}>{notification.id}</span>
              </Fact>
              <Fact label="User">
                <span className={idClass}>{notification.user_id ?? '—'}</span>
              </Fact>
              <Fact label="Member">
                <span className={idClass}>{notification.member_id ?? '—'}</span>
              </Fact>
              <Fact label="HOA">
                <span className={idClass}>{notification.hoa_id}</span>
              </Fact>
              <Fact label="Created">
                {formatTimestamp(notification.created_at)}
              </Fact>
              <Fact label="Updated">
                {formatTimestamp(notification.updated_at)}
              </Fact>
            </Section>
          </aside>
        </div>
      )}

      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogBody>
          Are you sure you want to delete this notification? This action cannot
          be undone.
        </DialogBody>
        <DialogActions>
          <Button
            color="zinc"
            onClick={() => setShowDeleteDialog(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </SidebarLayout>
  );
};

export default NotificationDetailPage;
